import { computed } from "vue";
import {
  isNavigationFailure,
  NavigationFailureType,
  type RouteLocationNormalized,
  type RouteLocationRaw,
  type Router,
  useRouter,
} from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import { SETUP_ITEM_COPY } from "@/copy/setupProgress";
import {
  type TourStatus,
  useInvalidateSetupProgress,
  useSaveTourProgressMutation,
  useTourProgressQuery,
} from "@/services/setup_progress";
import { useManageResourcesTracking, useTrack } from "@/services/gtm";
import { useResourcesQuery } from "@/services/resources";
import {
  type Profile,
  PROFILE_QUERY_KEY,
  resolveActiveOrganizationId,
} from "@/services/user";
import { useAuthStore } from "@/store/auth";
import type { TourContext, TourDefinition, TourStep } from "@/tours/types";
import {
  asList,
  findAnchor,
  isShown,
  nextFrame,
  SETTLE_INTERVAL_MS,
  waitForAnchor,
  waitForStableLayout,
} from "@/composables/guidedTour/anchors";
import {
  BUBBLE,
  bubbleButtons,
  bubbleInDialogTabOrder,
  controlsIn,
  createDriver,
  display,
  escapeHtml,
  focusBack,
  followLayout,
  hideStep,
  inNestedControl,
  pageFocus,
  RAISED_POPUPS,
  tearDownDriver,
} from "@/composables/guidedTour/bubble";
import {
  inTime,
  loadedRegistry,
  loadRegistryInTime,
  type Registry,
  registeredStep,
  registryIfLoaded,
} from "@/composables/guidedTour/loadRegistry";
import {
  createResume,
  writeResumeMarker,
} from "@/composables/guidedTour/resume";
import { createSaveFollowing } from "@/composables/guidedTour/saveFollowing";
import {
  activeTourKey,
  allowActionNavigation,
  failedLoads,
  resetRun,
  run,
  session,
  startingTour,
} from "@/composables/guidedTour/state";

// A page tour starts on the page as it is; a missing control won't turn up.
const PAGE_TOUR_ANCHOR_TIMEOUT_MS = 1000;
const PAGE_LOAD_WAIT_MS = 1500;
// How long a step's control may be gone, used, before what it opens shows.
const OPENING_WAIT_MS = 10000;
// A control gone longer than this after its click went for another reason.
const USED_CLICK_MS = 2000;

const watchedRouters = new WeakSet<Router>();

type TourEnd = "user" | "stuck" | "unavailable";
// Explanations shown in place of a tour; they don't count as having seen it.
const fallbackTours = new WeakSet<TourDefinition>();

const SIDEPANEL = '#sidepanel-heading, [aria-labelledby="sidepanel-heading"]';

// One in the side panel says nothing about this page.
function pageWall(): Element | null {
  for (const name of loadedRegistry().PAGE_TOUR_FALLBACK.anchors) {
    const wall = Array.from(
      document.querySelectorAll(`[data-tour="${name}"]`),
    ).find((el) => isShown(el) && !el.closest(SIDEPANEL));
    if (wall) return wall;
  }
  return null;
}

function fallbackFor(
  tour: TourDefinition,
  otherwise: { title: string; description: string } = loadedRegistry()
    .PAGE_TOUR_FALLBACK,
): TourDefinition {
  const { variants } = loadedRegistry().PAGE_TOUR_FALLBACK;
  const found = pageWall();
  const variant = found
    ? variants[found.getAttribute("data-tour") as keyof typeof variants]
    : undefined;
  const fallback: TourDefinition = {
    key: tour.key,
    finishLabel: "Got it",
    steps: [
      {
        anchor: found
          ? (found.getAttribute("data-tour") ?? undefined)
          : undefined,
        title: variant?.title ?? otherwise.title,
        description: variant?.description ?? otherwise.description,
      },
    ],
  };
  fallbackTours.add(fallback);
  return fallback;
}

function explanation(title: string, description: string): TourDefinition {
  const tour: TourDefinition = {
    key: "explanation",
    finishLabel: "Got it",
    steps: [{ title, description }],
  };
  fallbackTours.add(tour);
  return tour;
}

// Dropped up front so "Step 1 of N" counts what the user sees; after an
// action the page changes, so later steps are kept.
function onThisPage(tour: TourDefinition): TourDefinition {
  const firstAction = tour.steps.findIndex(
    (step) =>
      step.action &&
      !(step.optional && step.anchor && !findAnchor(step.anchor)),
  );
  const steps = tour.steps.filter(
    (step, index) =>
      (firstAction !== -1 && index >= firstAction) ||
      !step.anchor ||
      findAnchor(step.anchor),
  );
  return steps.length === tour.steps.length ? tour : { ...tour, steps };
}

/**
 * For UI that pops up on its own and shouldn't compete with a tour. Doesn't
 * set anything up, unlike useGuidedTour.
 */
export function useTourRunning() {
  return computed(() => activeTourKey.value !== null || startingTour.value);
}

// A page tour shows what the page can do, so ending it the moment the user
// tries something defeats it; a step opts out with `interactive: false`.
function usableStep(tour: TourDefinition, step: TourStep): TourStep {
  if (
    !tour.key.startsWith("page-") ||
    fallbackTours.has(tour) ||
    step.interactive !== undefined ||
    step.action ||
    step.advanceWhen
  ) {
    return step;
  }
  return { ...step, interactive: true, popups: step.popups ?? true };
}

function holdsAnchor(
  container: Element,
  anchor: string | string[] | undefined,
): boolean {
  // Hidden ones too: a dialog's other stage is still the dialog's.
  return asList(anchor).some(
    (name) => !!container.querySelector(`[data-tour="${name}"]`),
  );
}

function advancesOnTargetClick(step: TourStep): boolean {
  return !step.advanceWhen && !step.interactive && !step.tryIt;
}

export function useGuidedTour() {
  const router = useRouter();
  const progress = useTourProgressQuery();
  const queryClient = useQueryClient();
  const recheckProgress = useInvalidateSetupProgress();
  const authStore = useAuthStore();
  const resources = useResourcesQuery(() => authStore.isAuthed);
  const gtmResource = useManageResourcesTracking();
  const track = useTrack();
  const tourContext: TourContext = {
    organizationId: () => {
      const profile = queryClient.getQueryData<Profile>(PROFILE_QUERY_KEY);
      return profile ? resolveActiveOrganizationId(profile) : undefined;
    },
  };
  const { watchForAdvance, onSubmit, onLaterSave } = createSaveFollowing({
    queryClient,
    showStep,
    stop,
    giveUp,
  });
  const { resumeInterrupted } = createResume({
    progressRows: () => progress.data,
    isOnRoute,
    showStep,
  });

  function trackTour(
    event: "tour_started" | "tour_completed" | "tour_closed",
    tour: TourDefinition,
    end: { step: number; steps: number; reason: TourEnd | "" } | null = null,
  ) {
    // Every key every time: the data layer keeps a key until it's pushed
    // again, so one left out would carry over from the last event.
    track(event, {
      tour: tour.key,
      kind: tour.key.startsWith("page-") ? "page" : "checklist",
      step: end ? String(end.step) : "",
      steps: end ? String(end.steps) : "",
      reason: end?.reason ?? "",
    });
  }

  // `step` counts from 1; 0 for a tour left before anything showed.
  function trackEnd(
    step: number,
    reason: TourEnd,
    completed = false,
    steps = run.trackedTour?.steps.length ?? 0,
  ) {
    if (!run.trackedTour) return;
    const tour = run.trackedTour;
    run.trackedTour = null;
    trackTour(completed ? "tour_completed" : "tour_closed", tour, {
      step,
      steps,
      reason: completed ? "" : reason,
    });
  }

  function pickRoute(route: string | string[]): string {
    const options = Array.isArray(route) ? route : [route];
    const connected = new Set(resources.data?.map((r) => r.channel) ?? []);
    return (
      options.find((name) =>
        router.resolve({ name }).matched.every((record) => {
          const required = record.meta.requiredResources ?? [];
          return (
            required.length === 0 || required.some((c) => connected.has(c))
          );
        }),
      ) ?? options[0]
    );
  }

  function isOnRoute(route: string | string[]): boolean {
    const name = router.currentRoute.value.name;
    return (Array.isArray(route) ? route : [route]).some((r) => r === name);
  }
  const saveProgress = useSaveTourProgressMutation();

  // Saved against the registry's step numbers, since a page tour may drop
  // steps. An explanation isn't saved: the real tour hasn't been seen.
  function save(tour: TourDefinition, index: number, status: TourStatus) {
    if (fallbackTours.has(tour)) return;
    const step = registeredStep(tour, index);
    // A replay whose first step was skipped would otherwise leave the old
    // ending in place, since the server refuses anything else for it.
    if (!run.restarted && status === "in_progress" && step !== 0) {
      saveProgress.mutate({ tourKey: tour.key, step: 0, status });
    }
    run.restarted = true;
    saveProgress.mutate({ tourKey: tour.key, step, status });
  }

  function stop(
    tour: TourDefinition,
    step: number,
    status: TourStatus,
    reason: "user" | "stuck" = "user",
    // Another bubble takes over (a new tour, an explanation): focus stays,
    // and the run is left for it to carry on or reset.
    keepFocus = false,
  ) {
    const giveFocusBack = !keepFocus && !pageFocus() && !run.pointedInPage;
    // An explanation for a page with nothing to tour isn't a finish.
    if (fallbackTours.has(tour) && tour.key !== "explanation") {
      trackEnd(1, "unavailable", false, 0);
    } else {
      trackEnd(step + 1, reason, status === "completed", tour.steps.length);
    }
    const undo = run.undoArrival;
    run.undoArrival = null;
    undo?.();
    session.stepToken += 1;
    hideStep();
    activeTourKey.value = null;
    run.activeStep = null;
    run.currentStep = null;
    run.shownSteps = [];
    run.actionNavigationUntil = 0;
    run.savedStep = "";
    writeResumeMarker(null);
    tearDownDriver();
    if (giveFocusBack && !focusBack(run.returnFocus)) focusBack(run.lastTarget);
    // Nothing shown (left while it waited, the bubble's code didn't load):
    // the tour wasn't seen.
    if (run.stepsShown > 0) save(tour, step, status);
    if (!keepFocus) resetRun();
    // Some of what a tour walks through isn't saved through a mutation the
    // app-wide listener sees (dashboard autosave), so check once at the end.
    void recheckProgress();
  }

  // Leaving the page ends the tour, even when the step's control is on the
  // new page too or the step is still waiting: a tour carrying on where the
  // user didn't ask for it is worse than one that stops.
  if (!watchedRouters.has(router)) {
    watchedRouters.add(router);
    router.afterEach((to, from, failure) => {
      // Asked for on the page the user has now left.
      if (startingTour.value && !failure && to.path !== from.path) {
        session.runRequest += 1;
        startingTour.value = false;
      }
      // Still on the page: a guard kept the user here (a leave confirm), or
      // a newer navigation took over and reports for itself.
      if (isNavigationFailure(failure)) return;
      if (to.path === from.path || !run.currentStep) return;
      // A replaced step's navigation landing isn't the live step's own.
      const navigation =
        session.tourNavigation?.stepToken === session.stepToken
          ? session.tourNavigation
          : null;
      if (navigation && to.fullPath === navigation.path) {
        run.actionNavigationUntil = 0;
        return;
      }
      if (Date.now() < run.actionNavigationUntil) {
        run.actionNavigationUntil = 0;
        return;
      }
      // A redirect of the tour's own navigation is the tour not getting
      // there; anything else is the user leaving, also mid-navigation.
      stop(
        run.currentStep.tour,
        run.currentStep.index,
        "closed",
        navigation && to.redirectedFrom?.fullPath === navigation.path
          ? "stuck"
          : "user",
      );
    });
  }

  // "cancelled": another navigation overtook it (the user went elsewhere);
  // "failed": the page couldn't be opened (a stale chunk after a deploy).
  async function navigate(
    to: RouteLocationRaw,
    stepToken: number,
    // Runs before the page renders, so it's built in the state the tour expects.
    beforeRender?: () => void,
  ): Promise<"arrived" | "cancelled" | "failed"> {
    const navigation = {
      path: router.resolve(to).fullPath,
      stepToken,
    };
    session.tourNavigation = navigation;
    // The first navigation to start after the push; another to the same page
    // (the user clicking there) is a different one.
    let own: RouteLocationNormalized | null = null;
    const removeFirst = beforeRender
      ? router.beforeEach((destination) => {
          removeFirst?.();
          own = destination;
        })
      : undefined;
    const removeGuard = beforeRender
      ? router.beforeResolve((destination) => {
          // Both: a redirect or a held navigation can make another one the
          // first to reach beforeEach, and it may be going elsewhere.
          if (destination !== own || destination.fullPath !== navigation.path) {
            return;
          }
          removeGuard?.();
          beforeRender();
        })
      : undefined;
    try {
      const failure = await router.push(to);
      if (isNavigationFailure(failure, NavigationFailureType.cancelled)) {
        return "cancelled";
      }
      return isNavigationFailure(failure, NavigationFailureType.aborted)
        ? "failed"
        : "arrived";
    } catch {
      return "failed";
    } finally {
      removeFirst?.();
      removeGuard?.();
      if (session.tourNavigation === navigation) session.tourNavigation = null;
    }
  }

  function giveUp(
    tour: TourDefinition,
    index: number,
    why: { title: string; description: string } = loadedRegistry()
      .STEP_UNAVAILABLE,
  ) {
    stop(tour, index, "closed", "stuck", true);
    activeTourKey.value = "explanation";
    void showStep(explanation(why.title, why.description), 0);
  }

  function onTargetClick(tour: TourDefinition, index: number) {
    const isLast = index === tour.steps.length - 1;
    if (tour.steps[index].action) {
      allowActionNavigation();
      void showStep(tour, index + 1);
    } else {
      // The user took over (e.g. opened the dialog); the overlay mustn't cover it.
      stop(tour, index, isLast ? "completed" : "closed");
    }
  }

  // refind: the step's control was shown and has gone. An action step keeps
  // its waitMs, since the user may come back from another side-panel tab, and
  // not coming back is their choice rather than the tour failing.
  async function showStep(tour: TourDefinition, index: number, refind = false) {
    if (index >= tour.steps.length) {
      // Nothing shown (every control missing, a terms wall) isn't a finish.
      if (run.stepsShown === 0 && !fallbackTours.has(tour)) {
        void showStep(
          tour.key.startsWith("page-")
            ? fallbackFor(tour)
            : fallbackFor(tour, loadedRegistry().STEP_UNAVAILABLE),
          0,
        );
        return;
      }
      stop(tour, tour.steps.length - 1, "completed");
      return;
    }
    const token = ++session.stepToken;
    hideStep();
    run.currentStep = { tour, index };
    // `step` is how the step behaves; `authored` is what its author set, for
    // the checks that must not see a page tour's default interactivity.
    const authored = tour.steps[index];
    const step: TourStep = usableStep(tour, authored);
    const changesPage = !!step.route && !isOnRoute(step.route);
    if (changesPage || (step.anchor && !findAnchor(step.anchor))) {
      tearDownDriver();
    }
    const prepare = index === 0 && !run.arrived ? tour.onArrival : undefined;
    let prepared = false;
    if (changesPage && step.route) {
      const outcome = await navigate(
        { name: pickRoute(step.route) },
        token,
        prepare &&
          (() => {
            // A replaced step's page arriving late must not prepare it.
            if (token !== session.stepToken) return;
            prepared = true;
            run.arrived = true;
            run.undoArrival = prepare(tourContext) ?? null;
          }),
      );
      if (token !== session.stepToken) return;
      if (outcome === "cancelled") {
        stop(tour, index, "closed");
        return;
      }
      if (outcome === "failed") {
        giveUp(tour, index);
        return;
      }
    }
    if (prepare && !prepared) {
      try {
        run.arrived = true;
        run.undoArrival = prepare(tourContext) ?? null;
      } catch {
        giveUp(tour, index);
        return;
      }
    }
    const found = step.anchor
      ? await waitForAnchor(
          [...asList(step.anchor), ...asList(step.emptyAnchor)],
          (refind && !step.action ? undefined : step.waitMs) ??
            (tour.key.startsWith("page-")
              ? PAGE_TOUR_ANCHOR_TIMEOUT_MS
              : undefined),
        )
      : null;
    if (token !== session.stepToken) return;
    if (
      found &&
      asList(step.emptyAnchor).includes(found.getAttribute("data-tour") ?? "")
    ) {
      void showStep(tour, index + 1);
      return;
    }
    const element = found;
    // An explanation can be skipped; an action can't, because the steps after
    // it assume it happened.
    if (step.anchor && !element) {
      if (step.action && !step.optional && refind) {
        stop(tour, index, "closed");
      } else if (step.action && !step.optional) {
        giveUp(tour, index);
      } else {
        void showStep(tour, index + 1);
      }
      return;
    }
    if (element) {
      await waitForStableLayout(element);
      if (token !== session.stepToken) return;
      // Loading often swaps the control out (a list's button for the empty state's).
      if (!element.isConnected) {
        void showStep(tour, index, refind);
        return;
      }
    }
    run.activeStep = { tour, index };
    run.shownSteps = [
      ...run.shownSteps.filter((shown) => shown < index),
      index,
    ];

    if (!display.driver) {
      try {
        const [{ driver }] = await inTime(
          Promise.all([
            import("driver.js"),
            import("driver.js/dist/driver.css"),
            import("@/assets/styles/tour.css"),
          ]),
        );
        if (token !== session.stepToken) return;
        display.driver ??= createDriver(driver, () => {
          if (display.driver && run.activeStep) {
            stop(run.activeStep.tour, run.activeStep.index, "closed");
          }
        });
      } catch {
        // A stale chunk after a deploy: the tour mustn't stay "running".
        if (token === session.stepToken) {
          // Read first: stopping ends the run.
          const resumed = run.resumed;
          stop(tour, index, "closed", "stuck");
          if (!resumed) failedLoads.value += 1;
        }
        return;
      }
    }
    const activeDriver = display.driver;
    followLayout();
    document.body.classList.toggle(
      "fyr-tour-no-overlay",
      step.overlay === false,
    );
    const variant =
      step.variants?.[element?.getAttribute("data-tour") ?? ""] ?? {};
    const ends = !!variant.ends;
    const endReason = ends && !variant.chosen ? "stuck" : "user";
    const isLast = ends || index === tour.steps.length - 1;
    // Back only follows a Next: going back over an action would mean undoing
    // it (a dashboard created) or closing what it opened, which is the way out.
    const previous = run.shownSteps.at(-2);
    const backButton =
      previous !== undefined && !tour.steps[previous].action
        ? (["previous"] as const)
        : [];
    const copy = {
      ...step,
      ...variant,
      action: ends ? undefined : (variant.action ?? step.action),
    };
    document.body.classList.toggle(
      "fyr-tour-popups",
      !!step.popups || !!step.tryIt,
    );
    // The control the tour started from may be gone after a page change;
    // what the user used last stands in for it.
    if (!run.returnFocus?.isConnected) {
      run.returnFocus = pageFocus() ?? run.returnFocus;
    }
    run.lastTarget = element;
    activeDriver.highlight({
      element: element ?? undefined,
      popover: {
        title: escapeHtml(copy.title),
        description: [
          escapeHtml(copy.description),
          copy.action
            ? `<p class="fyr-tour-action">${escapeHtml(copy.action)}</p>`
            : "",
          step.tryIt
            ? `<p class="fyr-tour-action">Try it: ${escapeHtml(step.tryIt)}</p>`
            : "",
          `<p class="fyr-tour-progress">Step ${index + 1} of ${tour.steps.length}</p>`,
        ].join(""),
        side: step.side,
        // The last step always has a way to finish: doing its action still
        // does, and what's done is judged from the app's data, not the tour.
        showButtons:
          copy.action && !isLast
            ? [...backButton, "close"]
            : [...backButton, "next", "close"],
        prevBtnText: "Back",
        nextBtnText: isLast ? (tour.finishLabel ?? "Done") : "Next",
        onNextClick: () =>
          ends
            ? stop(tour, index, "closed", endReason)
            : void showStep(tour, index + 1),
        onPrevClick: () => {
          if (previous === undefined) return;
          run.shownSteps = run.shownSteps.filter((shown) => shown < previous);
          void showStep(tour, previous);
        },
        onCloseClick: () => stop(tour, index, "closed", endReason),
      },
    });
    if (run.savedStep !== `${tour.key}:${index}`) {
      run.savedStep = `${tour.key}:${index}`;
      writeResumeMarker(
        tour.steps[index].resumable && !fallbackTours.has(tour)
          ? {
              tourKey: tour.key,
              step: registeredStep(tour, index),
              at: Date.now(),
            }
          : null,
      );
      save(tour, index, "in_progress");
      run.stepsShown += 1;
    }
    // The control resizing (a popover's content) moves the cut-out too.
    if (element && typeof ResizeObserver !== "undefined") {
      const sized = new ResizeObserver(() => display.onLayoutChange?.());
      sized.observe(element);
      display.stepListeners.push(() => sized.disconnect());
    }
    if (step.overlay === false) {
      // driver.js blocks pointer events on everything but the target through
      // this class; a step without an overlay needs the page fully usable.
      document.body.classList.remove("driver-active");
    } else {
      // driver.js only adds it when a tour starts.
      document.body.classList.add("driver-active");
    }
    // driver.js traps Tab between the bubble and the highlight, which keeps
    // keyboard users out of the page on steps where they work in it.
    // Not a page tour's own steps: the rest of the page is dimmed and can't
    // be clicked, so Tab keeps to the highlight and the bubble there too.
    const freeTab =
      step.overlay === false ||
      !!authored.interactive ||
      !!step.tryIt ||
      !!step.advanceWhen?.closes;
    // driver.js's own trap puts the bubble's buttons first, so Tab from the
    // focused bubble would skip the highlight, and it leaves out custom
    // controls (a card with role="button").
    const driverTrap = (
      activeDriver.getState("__events") as
        | { onKeydown?: (event: KeyboardEvent) => void }
        | undefined
    )?.onKeydown;
    if (driverTrap) window.removeEventListener("keydown", driverTrap);
    function trapTab(event: KeyboardEvent) {
      if (event.key !== "Tab") return;
      const focused = document.activeElement;
      if (freeTab && !focused?.matches(BUBBLE)) return;
      // A menu or dialog raised from the highlight keeps its own Tab order.
      if (step.popups && focused?.closest(RAISED_POPUPS)) return;
      const cycle = [...controlsIn(element ?? undefined), ...bubbleButtons()];
      if (cycle.length === 0) return;
      event.preventDefault();
      const at = cycle.indexOf(focused as HTMLElement);
      const next = event.shiftKey
        ? (at <= 0 ? cycle.length : at) - 1
        : (at + 1) % cycle.length;
      cycle[next].focus();
    }
    window.addEventListener("keydown", trapTab);
    display.stepListeners.push(() =>
      window.removeEventListener("keydown", trapTab),
    );
    const releaseDialogTab =
      freeTab && element ? bubbleInDialogTabOrder(element) : null;
    if (releaseDialogTab) display.stepListeners.push(releaseDialogTab);
    if (freeTab || step.interactive) {
      // driver.js locks a scrollable parent of the highlight so the cut-out
      // can't drift; here the user needs to reach the rest of it (a side
      // panel's list). Only highlight() sets it, not refresh().
      document
        .querySelectorAll(".driver-active-element-parent-no-scroll")
        .forEach((parent) =>
          parent.classList.remove("driver-active-element-parent-no-scroll"),
        );
      // driver.js only follows the window scrolling.
      function onScroll() {
        display.onLayoutChange?.();
      }
      document.addEventListener("scroll", onScroll, {
        capture: true,
        passive: true,
      });
      display.stepListeners.push(() =>
        document.removeEventListener("scroll", onScroll, { capture: true }),
      );
    }
    // The step is on screen: from here on, leaving the page is the user's,
    // unless a save just went through and the app is about to open it.
    if (!run.actionNavigationAfterSave) run.actionNavigationUntil = 0;
    const form = tour.steps
      .slice(index)
      .find((later) => later.advanceWhen?.closes)?.advanceWhen?.closes;
    const inForm = !!form && !!element?.closest(`[data-tour="${form}"]`);
    const dialog = element?.closest('[role="dialog"]') ?? null;
    function lost() {
      if (run.submitsPending > 0) return;
      // Closed without saving (Cancel, Esc) is the user's choice.
      if (inForm && form && !findAnchor(form)) {
        stop(tour, index, "closed");
        return;
      }
      if (
        dialog &&
        !dialog.isConnected &&
        !(step.anchor && findAnchor(step.anchor))
      ) {
        stop(tour, index, "closed");
        return;
      }
      // A menu the previous step opened was closed unused, so reopen it; a
      // dialog still open has only switched tabs, so wait instead.
      const opener = run.shownSteps.at(-2);
      if (
        opener !== undefined &&
        tour.steps[opener].reopenable &&
        step.anchor &&
        !findAnchor(step.anchor) &&
        !(
          dialog?.isConnected && !holdsAnchor(dialog, tour.steps[opener].anchor)
        )
      ) {
        run.shownSteps = run.shownSteps.filter((shown) => shown < opener);
        void showStep(tour, opener);
        return;
      }
      // A last step has nothing to wait for (a side panel closed on its answer).
      if (
        index === tour.steps.length - 1 &&
        !step.action &&
        !(step.anchor && findAnchor(step.anchor))
      ) {
        stop(tour, index, "closed");
        return;
      }
      // It may skip itself (its emptyAnchor); Back mustn't land on it then.
      run.shownSteps = run.shownSteps.filter((shown) => shown < index);
      void showStep(tour, index, true);
    }
    let opening: ReturnType<typeof setTimeout> | null = null;
    let clickedAt = 0;
    if (element) {
      function noteClick() {
        clickedAt = Date.now();
      }
      element.addEventListener("click", noteClick, { capture: true });
      display.stepListeners.push(() =>
        element.removeEventListener("click", noteClick, { capture: true }),
      );
    }
    // An action can remove its own control (Customize hides as Home enters
    // edit mode), so what it opens is still worth waiting for.
    function usedUp(appears: string | string[]) {
      if (findAnchor(appears)) {
        allowActionNavigation();
        void showStep(tour, index + 1);
        return;
      }
      // Loading swapped the control for a new one.
      if (step.anchor && findAnchor(step.anchor)) {
        void showStep(tour, index);
        return;
      }
      // What it opens can't come from a dialog that has closed.
      if (dialog && !dialog.isConnected) {
        stop(tour, index, "closed");
        return;
      }
      if (Date.now() - clickedAt > USED_CLICK_MS) {
        lost();
        return;
      }
      if (opening) return;
      opening = setTimeout(lost, OPENING_WAIT_MS);
      display.stepListeners.push(() => {
        if (opening) clearTimeout(opening);
      });
    }
    // A v-show stage hidden (a dialog's next stage shown) is gone too.
    function gone() {
      return !!element && (!element.isConnected || !isShown(element));
    }
    display.onLayoutChange = () => {
      if (!gone()) {
        // Mid-move driver.js measures the previous control; the move ends
        // with a refresh of its own (onHighlighted).
        if (!display.driver?.getState("__transitionCallback")) {
          display.driver?.refresh();
        }
      } else if (step.advanceWhen?.appears && !step.advanceWhen.closes) {
        usedUp(step.advanceWhen.appears);
      } else if (!step.advanceWhen?.closes) {
        // A closing form is what its step waits for.
        lost();
      }
    };
    // A step waiting for something (a form to close, a control to be used)
    // expects its control to go away; its own watcher handles that.
    display.onContentChange =
      step.advanceWhen && !ends
        ? null
        : () => {
            if (gone()) lost();
          };
    if (ends) {
      // What it highlights (Create Plan) would open under the overlay, where
      // nothing responds: the tour gets out of the way instead. Others (a
      // terms wall) stay, so accepting re-finds the real step.
      if (
        element &&
        variant.endsOnClick &&
        !authored.interactive &&
        !step.tryIt
      ) {
        function endOnClick() {
          stop(tour, index, "closed", endReason);
        }
        element.addEventListener("click", endOnClick, {
          capture: true,
          once: true,
        });
        display.stepListeners.push(() =>
          element.removeEventListener("click", endOnClick, { capture: true }),
        );
      }
      return;
    }
    document.addEventListener("submit", onSubmit, { capture: true });
    document.addEventListener("click", onLaterSave, { capture: true });
    display.stepListeners.push(() => {
      document.removeEventListener("submit", onSubmit, { capture: true });
      document.removeEventListener("click", onLaterSave, { capture: true });
    });
    if (step.advanceWhen) {
      watchForAdvance(tour, index, step.advanceWhen);
    } else if (element && advancesOnTargetClick(step)) {
      // Capture phase: runs before a link's own handler navigates away.
      function handler(event: Event) {
        if (step.action && element && inNestedControl(event.target, element)) {
          return;
        }
        onTargetClick(tour, index);
      }
      element.addEventListener("click", handler, { capture: true });
      display.stepListeners.push(() =>
        element.removeEventListener("click", handler, { capture: true }),
      );
    }
  }

  function start(key: string) {
    const origin = pageFocus();
    const request = ++session.runRequest;
    const registry = registryIfLoaded();
    if (registry) {
      startingTour.value = false;
      begin(registry, key, origin);
      return;
    }
    startingTour.value = true;
    loadRegistryInTime().then(
      (loaded) => {
        if (request !== session.runRequest) return;
        startingTour.value = false;
        begin(loaded, key, origin);
      },
      () => {
        if (request !== session.runRequest) return;
        startingTour.value = false;
        if (SETUP_ITEM_COPY[key]) openItemPage(key);
        else failedLoads.value += 1;
      },
    );
  }

  function openItemPage(key: string) {
    const copy = SETUP_ITEM_COPY[key];
    if (copy) void router.push(copy.to);
  }

  function begin(loaded: Registry, key: string, origin: HTMLElement | null) {
    const tour = loaded.TOURS[key];
    if (!tour) {
      openItemPage(key);
      return;
    }
    // Being walked through connecting is the start of connecting, wherever
    // it was started from.
    if (key === "connect_channels" && activeTourKey.value !== key) {
      gtmResource.startCreating();
    }
    if (run.currentStep) {
      stop(run.currentStep.tour, run.currentStep.index, "closed", "user", true);
    }
    // The replaced tour's page may still be loading. Picking a tour on the
    // page on screen is the user's newest choice, so it overtakes whatever is
    // still pending, theirs included, rather than be pulled away when it lands.
    if (session.tourNavigation) {
      void router.replace(router.currentRoute.value.fullPath);
    }
    // Also ends one started but not yet showing (a page tour still waiting).
    trackEnd(0, "user");
    resetRun({ returnFocus: origin, trackedTour: tour });
    trackTour("tour_started", tour);
    activeTourKey.value = key;
    // Always from the start; an external sign-in is the only resume.
    if (key.startsWith("page-")) {
      void startPageTour(tour);
    } else {
      void showStep(tour, 0);
    }
  }

  async function startPageTour(tour: TourDefinition) {
    const token = ++session.stepToken;
    const path = router.currentRoute.value.path;
    // The page may still be loading the data its controls depend on.
    const waitUntil = Date.now() + PAGE_LOAD_WAIT_MS;
    while (queryClient.isFetching() > 0 && Date.now() < waitUntil) {
      await new Promise((resolve) => setTimeout(resolve, SETTLE_INTERVAL_MS));
    }
    await nextFrame();
    if (token !== session.stepToken || activeTourKey.value !== tour.key) return;
    if (router.currentRoute.value.path !== path) {
      activeTourKey.value = null;
      trackEnd(0, "user");
      resetRun();
      return;
    }
    // Behind a setup, plan or terms wall with none of the tour's controls:
    // explain instead. Recommendations' intro is a wall its tour points into.
    if (pageWall() && !findAnchor(tour.steps.flatMap((s) => s.anchor ?? []))) {
      void showStep(fallbackFor(tour), 0);
      return;
    }
    void showStep(onThisPage(tour), 0);
  }

  function hasSeen(key: string): boolean {
    return progress.data?.some((r) => r.tourKey === key) ?? false;
  }

  return {
    start,
    resumeInterrupted,
    hasSeen,
    activeTourKey,
    failedLoads,
    isProgressLoaded: () => progress.isSuccess,
  };
}
